import { customFunctions } from "@/config/customFunction";
import { useModal } from "@/features/modal/ModalStackManager";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { TransportationsAutoComplete } from "@/features/ui/autoComplete/transportation/transportation-autoComplete";
import { Button, Checkbox, Flex, NumberInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import dayjs from "dayjs";
import { MantineReactTable, MRT_ColumnDef } from "mantine-react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PurchaseOrderInventoriesAutoComplete } from "../ui/autoComplete/inventoryLot/purchaseInventory-autoComplete";
import { LotSelectComplete } from "../ui/autoComplete/lot/lot-\bselectComplete";
import { StockPlanAutoComplete } from "../ui/autoComplete/stockPlan/stockPlan-autoComplete";
import { usePurchaseBatchOutgoingLogic } from "./hook/usePurchaseBatchOutgoing";

interface PurchaseOrderOutgoingFormProps {
  purchaseOrder?: () => (
    | PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner
    | undefined
  )[];
  purchaseOrderItems?: AuthSignupPost201ResponseEquipmentPurchaseOrderItem[] | undefined;
}

export const PurchaseOrderOutgoingForm = (params: PurchaseOrderOutgoingFormProps) => {
  const { purchaseOrder, purchaseOrderItems } = params;
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState<Record<string, boolean>>({});

  const { form, onSubmit, setLotId, selectedItems } = usePurchaseBatchOutgoingLogic({
    purchaseOrder,
    purchaseOrderItems,
  });

  const columns: MRT_ColumnDef<PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner>[] = [
    {
      accessorFn: (row) => (purchaseOrder ? row.purchaseOrder?.code : row.purchaseOrderCode),
      header: t("purchaseOrder.code"),
      size: 150,
    },
    {
      accessorKey: purchaseOrder ? "item.name" : "itemName",
      header: t("item.name"),
      size: 150,
    },
    {
      accessorKey: "quantity",
      header: t("purchaseOrder.targetQuantity"),
      size: 100,
    },
    {
      accessorKey: "sumOfDeliveredQuantity",
      header: t("purchaseOrderItems.sumOfDeliveredQuantity"),
      size: 100,
    },
    {
      accessorKey: "sourceLocationCode",
      header: t("inventory.fromLocation"),
      size: 200,
      Cell: ({ row }) => {
        const sourceLocationCode = form.getInputProps(`items.${row.index}.locationCode`).value;
        return (
          <>
            {isChecked[row.index] ? (
              <LocationAutoComplete disabled value={sourceLocationCode} />
            ) : (
              <LocationAutoComplete {...form.getInputProps(`items.${row.index}.locationCode`)} />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "lotId",
      header: t("lot.lotInfo"),
      size: 200,
      Cell: ({ row }) => {
        const locationCode = form.getInputProps(`items.${row.index}.locationCode`).value;
        const lotId = form.getInputProps(`items.${row.index}.lotId`).value ?? "-";

        return (
          <>
            {isChecked[row.index] ? (
              <LotSelectComplete
                disabled
                itemCodeValue={row.original.itemCode ?? ""}
                newIdValue={Number(lotId)}
                {...form.getInputProps(`items.${row.index}.lotId`)}
              />
            ) : (
              <PurchaseOrderInventoriesAutoComplete
                itemCodes={row.original.itemCode ?? ""}
                locationCode={locationCode}
                {...form.getInputProps(`items.${row.index}.lotId`)}
                onChange={(e) => {
                  form.setFieldValue(`items.${row.index}.lotId`, e);
                  setLotId({ lotId: e, index: row.index });
                }}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "inputQuantity",
      header: t("purchaseOrder.deliveryQuantity"),
      size: 200,
      Cell: ({ row }) => {
        const quantity = row.original.quantity;
        const deliveryQuantity = row.original.sumOfDeliveredQuantity ?? 0;
        return (
          <NumberInput
            onChange={(val) =>
              form.setFieldValue(
                `items.${row.index}.inputQuantity`,
                val === "" || val === undefined ? "" : val.toString(),
              )
            }
            defaultValue={Number(quantity) - Number(deliveryQuantity)}
            max={Number(quantity) - Number(deliveryQuantity)}
          />
        );
      },
    },
    {
      accessorKey: "stockPlanId",
      header: t("stockPlan.stockPlan"),
      size: 200,
      Cell: ({ row }) => {
        return (
          <Flex direction={"column"} gap="xs">
            <Checkbox
              label="재고계획 사용하기"
              checked={isChecked[row.index] ?? false}
              onChange={(e) => {
                setIsChecked((prev) => ({
                  ...prev,
                  [row.index]: e.target.checked,
                }));
                if (!e.target.checked) {
                  form.setFieldValue(`items.${row.index}.stockPlanId`, "");
                }
              }}
            />
            {isChecked[row.index] ? (
              <StockPlanAutoComplete
                disabled={!isChecked[row.index]}
                purchaseOrderItemId={
                  isChecked[row.index]
                    ? form.values.items[row.index].purchaseOrderItemId
                    : undefined
                }
                {...form.getInputProps(`items.${row.index}.stockPlanId`)}
              />
            ) : null}
          </Flex>
        );
      },
    },
    ...(customFunctions.ADD_PURCHASE_ORDER_INVOICE_DATE
      ? [
          {
            accessorKey: "invoiceDate",
            header: t("purchaseOrder.invoiceDate"),
            size: 200,
            Cell: ({ row }: any) => {
              return (
                <DateInput
                  label={t("purchaseOrder.invoiceDate")}
                  valueFormat="YYYY-MM-DD"
                  //   value={dayjs(form.values.invoiceDate).toDate()}
                  onChange={(e) => {
                    form.setFieldValue(
                      `items.${row.index}.invoiceDate`,
                      dayjs(e).startOf("day")?.toISOString(),
                    );
                  }}
                />
              );
            },
          },
        ]
      : []),
    {
      accessorKey: "transportationId",
      header: t("transportation.code"),
      size: 250,
      Cell: ({ row }) => {
        return (
          <TransportationsAutoComplete
            // disabled={!isChecked[row.index]}
            partnerCode={row.original.partnerCode}
            // data={transportationOptions}
            {...form.getInputProps(`items.${row.index}.transportationId`)}
          />
        );
      },
    },
  ];

  return (
    <Flex direction="column" gap="md">
      <MantineReactTable
        columns={columns}
        data={form.values.items ?? []}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enablePagination={false}
      />
      <Flex justify="flex-end" gap="md">
        <Flex gap="md">
          <Button onClick={closeModal} color="gray">
            {t("common.cancel")}
          </Button>
          <Button onClick={onSubmit}>{t("delivery.delivery")}</Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
